import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiSave, FiUpload, FiX, FiImage } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { today } from '../../utils/format';

const emptyForm = {
  machine_id: '', name: '', cnic: '', phone: '', address: '', employee_type: 'operator',
  designation: '', shift_id: '', department_id: '', basic_salary: '', joining_date: today(), is_active: true,
  cnic_front: '', cnic_back: '',
};

function CNICUpload({ label, value, onChange, error, inputRef }) {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showError('Image too large', 'Must be under 2 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="form-group">
      <label className="form-label">{label} <span className="req">*</span></label>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-secondary btn-sm" type="button" onClick={() => inputRef.current?.click()}>
          <FiUpload /> Choose Image
        </button>
        {value && (
          <button className="btn btn-danger-outline btn-sm" type="button" onClick={() => onChange('')}>
            <FiX /> Remove
          </button>
        )}
      </div>
      {error && <div className="invalid-feedback" style={{ display: 'block' }}>{error}</div>}
      {value && (
        <div style={{ marginTop: 8, padding: 6, background: '#f9fafb', borderRadius: 6, border: '1px solid var(--border)', display: 'inline-block' }}>
          <img src={value} alt={label} style={{ maxWidth: 200, maxHeight: 120, objectFit: 'contain', borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const frontRef = useRef(null);
  const backRef = useRef(null);

  useEffect(() => {
    const promises = [
      api.shifts.list({ per_page: 500 }).then(({ data }) => {
        const paginated = data.shifts || data.data || data;
        const raw = paginated?.data || paginated;
        setShifts(Array.isArray(raw) ? raw : []);
      }),
      api.departments.list({ per_page: 500, active_only: 1 }).then(({ data }) => {
        const paginated = data.departments || data.data || data;
        const raw = paginated?.data || paginated;
        setDepartments(Array.isArray(raw) ? raw : []);
      }),
    ];

    if (isEdit) {
      promises.push(
        api.employees.get(id).then(({ data }) => {
          const e = data.employee || data;
          setForm({
            machine_id: e.machine_id || '',
            name: e.name || '',
            cnic: e.cnic || '',
            phone: e.phone || '',
            address: e.address || '',
            employee_type: e.employee_type || 'operator',
            designation: e.designation || '',
            shift_id: e.shift_id || '',
            department_id: e.department_id || '',
            basic_salary: e.basic_salary || '',
            joining_date: e.joining_date ? String(e.joining_date).slice(0, 10) : today(),
            is_active: e.is_active !== false,
            cnic_front: e.cnic_front || '',
            cnic_back: e.cnic_back || '',
          });
        })
      );
    }

    Promise.all(promises)
      .catch((err) => showError('Failed to load data', extractError(err)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const formatCNIC = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 13);
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return digits.slice(0, 5) + '-' + digits.slice(5);
    return digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12);
  };

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Name is required';
    if (!form.employee_type) errs.employee_type = 'Type is required';
    if (!form.cnic) errs.cnic = 'CNIC is required';
    else if (!/^\d{5}-\d{7}-\d$/.test(form.cnic)) errs.cnic = 'CNIC must be in format XXXXX-XXXXXXX-X';
    if (!form.phone) errs.phone = 'Phone is required';
    if (!form.designation) errs.designation = 'Designation is required';
    if (!form.basic_salary) errs.basic_salary = 'Basic salary is required';
    if (!form.joining_date) errs.joining_date = 'Joining date is required';
    if (!form.address) errs.address = 'Address is required';
    if (!form.cnic_front) errs.cnic_front = 'CNIC front image is required';
    if (!form.cnic_back) errs.cnic_back = 'CNIC back image is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        machine_id: form.machine_id,
        name: form.name,
        cnic: form.cnic,
        phone: form.phone,
        address: form.address,
        employee_type: form.employee_type,
        designation: form.designation,
        shift_id: form.shift_id || null,
        department_id: form.department_id || null,
        basic_salary: Number(form.basic_salary) || 0,
        joining_date: form.joining_date,
        is_active: form.is_active,
        cnic_front: form.cnic_front,
        cnic_back: form.cnic_back,
      };
      if (isEdit) {
        await api.employees.update(id, payload);
        toast('success', '', 'Employee updated successfully');
      } else {
        await api.employees.create(payload);
        toast('success', '', 'Employee created successfully');
      }
      navigate('/employees');
    } catch (err) {
      showError('Failed to save employee', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div></div>;

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Employee' : 'New Employee'}
        subtitle={isEdit ? `Editing ${form.name}` : 'Add a new employee record.'}
        crumbs={[{ label: 'HR', to: '/employees' }, { label: 'Employees', to: '/employees' }, { label: isEdit ? 'Edit' : 'New Employee' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/employees')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><FiLoader /> Saving...</> : <><FiSave /> {isEdit ? 'Update' : 'Create'} Employee</>}
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-header"><div className="card-title">Basic Information</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Machine ID</label>
              <input className={`form-control ${errors.machine_id ? 'is-invalid' : ''}`} value={form.machine_id} onChange={(e) => { set('machine_id', e.target.value); if (errors.machine_id) setErrors((p) => ({ ...p, machine_id: '' })); }} placeholder="e.g. M-001" />
              {errors.machine_id && <div className="invalid-feedback">{errors.machine_id}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Name <span className="req">*</span></label>
              <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { set('name', e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="Full name" />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Type <span className="req">*</span></label>
              <SearchableSelect options={[{ value: 'operator', label: 'Operator' }, { value: 'helper', label: 'Helper' }, { value: 'staff', label: 'Staff' }]} value={form.employee_type} onChange={(v) => { set('employee_type', v); if (errors.employee_type) setErrors((p) => ({ ...p, employee_type: '' })); }} isInvalid={!!errors.employee_type} placeholder="-- Select --" />
              {errors.employee_type && <div className="invalid-feedback">{errors.employee_type}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Designation <span className="req">*</span></label>
              <input className={`form-control ${errors.designation ? 'is-invalid' : ''}`} value={form.designation} onChange={(e) => { set('designation', e.target.value); if (errors.designation) setErrors((p) => ({ ...p, designation: '' })); }} placeholder="Designation" />
              {errors.designation && <div className="invalid-feedback">{errors.designation}</div>}
            </div>
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">CNIC <span className="req">*</span></label>
              <input className={`form-control ${errors.cnic ? 'is-invalid' : ''}`} value={form.cnic} onChange={(e) => { set('cnic', formatCNIC(e.target.value)); if (errors.cnic) setErrors((p) => ({ ...p, cnic: '' })); }} placeholder="XXXXX-XXXXXXX-X" maxLength={15} />
              {errors.cnic && <div className="invalid-feedback">{errors.cnic}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Phone <span className="req">*</span></label>
              <input className={`form-control ${errors.phone ? 'is-invalid' : ''}`} value={form.phone} onChange={(e) => { set('phone', e.target.value); if (errors.phone) setErrors((p) => ({ ...p, phone: '' })); }} placeholder="Phone number" />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Shift</label>
              <SearchableSelect options={[{ value: '', label: '— None —' }, ...shifts.filter((s) => s.is_active !== false).map((s) => ({ value: s.id, label: s.name }))]} value={form.shift_id} onChange={(v) => set('shift_id', v)} placeholder="-- Optional --" />
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <SearchableSelect options={[{ value: '', label: '— None —' }, ...departments.map((d) => ({ value: d.id, label: d.name }))]} value={form.department_id} onChange={(v) => set('department_id', v)} placeholder="-- Optional --" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Basic Salary <span className="req">*</span></label>
              <input className={`form-control ${errors.basic_salary ? 'is-invalid' : ''}`} type="number" value={form.basic_salary} onChange={(e) => { set('basic_salary', e.target.value); if (errors.basic_salary) setErrors((p) => ({ ...p, basic_salary: '' })); }} placeholder="Monthly salary" />
              {errors.basic_salary && <div className="invalid-feedback">{errors.basic_salary}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Joining Date <span className="req">*</span></label>
              <input className={`form-control ${errors.joining_date ? 'is-invalid' : ''}`} type="date" value={form.joining_date} onChange={(e) => { set('joining_date', e.target.value); if (errors.joining_date) setErrors((p) => ({ ...p, joining_date: '' })); }} />
              {errors.joining_date && <div className="invalid-feedback">{errors.joining_date}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address <span className="req">*</span></label>
            <input className={`form-control ${errors.address ? 'is-invalid' : ''}`} value={form.address} onChange={(e) => { set('address', e.target.value); if (errors.address) setErrors((p) => ({ ...p, address: '' })); }} placeholder="Full address" />
            {errors.address && <div className="invalid-feedback">{errors.address}</div>}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginTop: 4 }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} /> Active employee
          </label>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title"><FiImage /> CNIC Images</div></div>
        <div className="card-body">
          <div className="grid-2">
            <CNICUpload label="CNIC Front" value={form.cnic_front} onChange={(v) => { set('cnic_front', v); if (errors.cnic_front) setErrors((p) => ({ ...p, cnic_front: '' })); }} error={errors.cnic_front} inputRef={frontRef} />
            <CNICUpload label="CNIC Back" value={form.cnic_back} onChange={(v) => { set('cnic_back', v); if (errors.cnic_back) setErrors((p) => ({ ...p, cnic_back: '' })); }} error={errors.cnic_back} inputRef={backRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
